import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { filterPaymentAccounts } from 'components/common/PaymentForm/utils';
import i18nContext from 'components/i18n-context';
import { getAccountTitleWithFormatting } from 'components/utils';
import { amountFormattedValue, isCryptoProvider } from 'services/utils';
import Button from 'uikit/Button/Button';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import './PopUpScheme.scss';

const PopUpUseTemplateScheme = ({
  onClose,
  onConfirm,
  isLoading,
  title,
  closeButtonLabel,
  confirmButtonLabel,
  userAccounts,
  templateData,
  availableBalanceDetails,
  customerNumber,
  paymentStore
}) => {
  const i18n = useContext(i18nContext);
  const providerType = paymentStore.currentAccount?.transfer_provider;
  const [paymentMethodError, setPaymentMethodError] = useState(null);

  const filteredAccounts = filterPaymentAccounts(userAccounts)?.filter(
    (account) => account?.currency === templateData?.currency
  );

  const accountsOptions = filteredAccounts?.map((account) => {
    const title = getAccountTitleWithFormatting(account);

    return {
      key: account?.wallet_number,
      value: title,
      currency: account?.currency,
      networkCode: account?.network,
      paymentMethods: account?.payment_methods || []
    };
  });

  const noWalletsError = accountsOptions.length === 0 ? i18n.getMessage('templates.popUp.useTemplate.noWallets') : null;

  const getAvailableBalance = () =>
    paymentStore?.currentAccount &&
    i18n.getMessage('sendMoney.topLabel', {
      available:
        availableBalanceDetails?.displayAmount || amountFormattedValue(paymentStore?.currentAccount.available, 2)
    });

  // Checking the available balance of the account selected by the user
  useEffect(() => {
    if (paymentStore.currentAccount?.wallet_number) {
      paymentStore.validateTemplatePaymentAmount(customerNumber, templateData.templateId, {
        accountNumber: paymentStore.currentAccount?.wallet_number
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.currentAccount?.wallet_number]);

  // Preselecting account
  useEffect(() => {
    paymentStore.setSelectedAccount(null);

    if (accountsOptions.length > 0) {
      const firstAccount = filteredAccounts[0];
      handleAccountChange('', firstAccount?.wallet_number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = (name, data) => {
    const selectedAccount = filterPaymentAccounts(userAccounts).find(({ wallet_number }) => wallet_number === data);

    if (
      paymentStore.currentAccount?.wallet_number !== selectedAccount?.wallet_number ||
      (!paymentStore.error && paymentStore?.availablePaymentMethods?.length === 0)
    ) {
      paymentStore.setAccountInfo(selectedAccount);

      // Check if the selected account supports the template's payment method
      const templatePaymentMethod = templateData?.paymentMethod;
      if (templatePaymentMethod && selectedAccount?.payment_methods) {
        const supportedMethods = selectedAccount.payment_methods || [];
        if (!supportedMethods.includes(templatePaymentMethod)) {
          setPaymentMethodError(
            i18n.getMessage('templates.popUp.useTemplate.unsupportedPaymentMethod', {
              method: templatePaymentMethod
            })
          );
        } else {
          setPaymentMethodError(null);
        }
      } else {
        setPaymentMethodError(null);
      }
    }
  };

  return (
    <div className={'scheme-wrapper use-template'}>
      <div className={'scheme-header'}>
        <p className={'title'}>{title}</p>
        <CloseIcon className={'scheme-close-icon'} onClick={onClose} />
      </div>

      <div className={'scheme-content'}>
        <InputDropDown
          isRequired={true}
          bottomLabel={getAvailableBalance()}
          label={i18n.getMessage('transfer.form.accountNumber.label')}
          value={paymentStore.currentAccount?.wallet_number}
          currency={paymentStore.currentAccount?.currency}
          networkCode={paymentStore.currentAccount?.network || null}
          onChange={handleAccountChange}
          options={accountsOptions}
          isMulti={false}
          isCryptoWallet={isCryptoProvider(providerType)}
          error={noWalletsError || paymentMethodError || paymentStore.error?.message}
        />
      </div>

      <div className={'scheme-footer'}>
        <Button
          className={'confirm-button'}
          type={'primary'}
          size={'large'}
          isLoading={isLoading}
          onClick={() => onConfirm(paymentStore.currentAccount?.wallet_number)}
          isDisabled={isLoading || !paymentStore.isTemplateReadyToUse || noWalletsError || paymentMethodError}
        >
          {confirmButtonLabel}
        </Button>

        <Button
          className={'cancel-button'}
          type={'outline'}
          size={'large'}
          isLoading={isLoading}
          onClick={onClose}
          isDisabled={isLoading}
        >
          {closeButtonLabel}
        </Button>
      </div>
    </div>
  );
};

PopUpUseTemplateScheme.propTypes = {
  title: PropTypes.string,
  closeButtonLabel: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  userAccounts: PropTypes.array,
  templateData: PropTypes.object,
  availableBalanceDetails: PropTypes.object,
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(PopUpUseTemplateScheme));
